import * as React from "react";
import * as RTooltip from "@radix-ui/react-tooltip";

export function Tooltip({
  label,
  children,
  side = "top",
  contentClassName,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  contentClassName?: string;
}) {
  // Radix's default open triggers are hover + focus - on a touch device
  // there's no hover, and a tap only focuses the trigger if it's already a
  // real focusable element (a plain non-interactive <span>, e.g. a status
  // badge, never gets focus from a tap at all). Net effect: every
  // Tooltip-wrapped badge in the app was inert on mobile - tapping it did
  // nothing, with no way to see what it meant. Controlled `open` here adds
  // an explicit tap-to-toggle on top of Radix's existing hover/focus
  // handling (onOpenChange still fires for those, e.g. blur closing it on
  // mobile when focus moves elsewhere) rather than replacing it.
  //
  // preventDefault() is load-bearing, not decorative: Radix's own
  // TooltipTrigger wires its onClick as
  // composeEventHandlers(props.onClick, context.onClose) (see
  // @radix-ui/react-tooltip's source) - our handler runs first and toggles
  // `open` true, but Radix's composed handler then calls context.onClose()
  // right after (in the same synchronous event) unless the event was
  // marked defaultPrevented, which immediately flips `open` back to false
  // before paint. Without this line every tap silently no-ops - the exact
  // "does nothing when tapped" bug this whole hook exists to fix.
  const [open, setOpen] = React.useState(false);
  return (
    <RTooltip.Provider delayDuration={250}>
      <RTooltip.Root open={open} onOpenChange={setOpen}>
        <RTooltip.Trigger
          asChild
          onClick={(event) => {
            event.preventDefault();
            setOpen((o) => !o);
          }}
        >
          {children}
        </RTooltip.Trigger>
        <RTooltip.Portal>
          <RTooltip.Content
            side={side}
            sideOffset={6}
            className={
              "z-50 max-w-xs whitespace-pre-line rounded-md bg-ink px-2 py-1 text-xs font-medium leading-snug text-white shadow-overlay animate-fade-in " +
              (contentClassName ?? "")
            }
          >
            {label}
            <RTooltip.Arrow className="fill-[var(--bg-inverse)]" />
          </RTooltip.Content>
        </RTooltip.Portal>
      </RTooltip.Root>
    </RTooltip.Provider>
  );
}

/** Icon-only button wrapper enforcing aria-label + tooltip per spec 4.6. */
export function IconButton({
  label,
  onClick,
  children,
  className,
}: {
  label: string;
  onClick?: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <Tooltip label={label}>
      <button
        type="button"
        aria-label={label}
        onClick={onClick}
        className={
          "inline-flex h-8 w-8 items-center justify-center rounded-md text-faint transition-colors hover:bg-subtle hover:text-ink " +
          (className ?? "")
        }
      >
        {children}
      </button>
    </Tooltip>
  );
}
